import { toSafeHttpUrl, toSafeMailtoUrl } from "@/lib/url-safety";

import type { CommercialTenantDetail } from "./commercial-tenant-card.types";

/**
 * Minimum digit count for a viable WhatsApp link. Most international numbers
 * are at least 10 digits (country code + national number); shorter values
 * are stub data the editor entered before saving.
 */
export const MIN_WHATSAPP_DIGITS = 10;

/**
 * Build a `https://wa.me/<digits>` href from a CMS-provided phone number,
 * returning null when the stripped digit count is below
 * `MIN_WHATSAPP_DIGITS`. The result is the single source of truth for
 * "does this tenant have a usable WhatsApp link" — used both for the
 * card-level enable check and for rendering the icon in the detail
 * panel, so they can't drift.
 */
export function toWhatsappHref(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= MIN_WHATSAPP_DIGITS
    ? `https://wa.me/${digits}`
    : null;
}

/**
 * Derived flags + resolved contact hrefs computed from a
 * `CommercialTenantDetail`. The card uses `isNotEmpty` to decide
 * between dialog and static-article mode; the panel uses every other
 * field to gate its section/icon rendering. Single source of truth so
 * the two can't disagree.
 */
export interface ResolvedTenantPanel {
  /** Validated `https://wa.me/<digits>` link, or null when phone is missing/short. */
  whatsappHref: string | null;
  /** Validated `mailto:` link, or null when email is missing/malformed. */
  emailHref: string | null;
  /** Sanitised website URL (http/https only), or null when missing/unsafe. */
  websiteHref: string | null;
  /** Sanitised Facebook URL, or null when missing/unsafe. */
  facebookHref: string | null;
  /** Sanitised Instagram URL, or null when missing/unsafe. */
  instagramHref: string | null;
  /** True when `address` is a non-empty portable-text array. */
  hasAddress: boolean;
  /** True when `tradingHours` is a non-empty portable-text array. */
  hasTradingHours: boolean;
  /** True when `bodyCopy` is a non-empty portable-text array. */
  hasBodyCopy: boolean;
  /** True when either address or tradingHours has content — gates the address row wrapper. */
  showAddressRow: boolean;
  /** True when at least one contact icon link is valid. */
  hasContactIcons: boolean;
  /**
   * True when the panel has at least one section worth rendering. The
   * card reads this flag to choose between dialog mode (interactive)
   * and static `<article>` mode.
   */
  isNotEmpty: boolean;
}

/**
 * Resolve a `CommercialTenantDetail` into derived flags and validated
 * contact hrefs. Used by both `CommercialTenantCard` (for the dialog
 * enable check) and `CommercialTenantDetailPanel` (for per-section
 * rendering) so the two stay in lock-step.
 */
export function resolvePanelContent(
  detail: CommercialTenantDetail,
): ResolvedTenantPanel {
  const websiteHref = toSafeHttpUrl(detail.website);
  const emailHref = toSafeMailtoUrl(detail.email);
  const whatsappHref = toWhatsappHref(detail.phone);
  const facebookHref = toSafeHttpUrl(detail.facebook);
  const instagramHref = toSafeHttpUrl(detail.instagram);

  const hasAddress = Boolean(detail.address && detail.address.length > 0);
  const hasTradingHours = Boolean(
    detail.tradingHours && detail.tradingHours.length > 0,
  );
  const hasBodyCopy = Boolean(detail.bodyCopy && detail.bodyCopy.length > 0);
  const showAddressRow = hasAddress || hasTradingHours;
  const hasContactIcons = Boolean(
    whatsappHref || emailHref || websiteHref || facebookHref || instagramHref,
  );

  return {
    whatsappHref,
    emailHref,
    websiteHref,
    facebookHref,
    instagramHref,
    hasAddress,
    hasTradingHours,
    hasBodyCopy,
    showAddressRow,
    hasContactIcons,
    isNotEmpty: showAddressRow || hasBodyCopy || hasContactIcons,
  };
}
